import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnDestroy,
    Signal,
    signal,
    viewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { NgApexchartsModule } from 'ng-apexcharts';

import { BaseChartOptions, ChartTypeEnum } from '@connect-services/client/services/chart.service';
import { ressourcesModules } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgApexchartsModule, ressourcesModules],
    selector: 'app-base-chart',
    styleUrl: './base-chart.component.scss',
    templateUrl: './base-chart.component.html',
})
export class BaseChartComponent implements AfterViewInit, OnDestroy {
    public readonly chartWidth = signal<string>('100%');
    public readonly finalOptions = computed<BaseChartOptions>(() => {
        const options = this.options();

        return {
            ...options,
            chart: {
                ...options.chart,
                width: this.chartWidth(),
            },
            dataLabels: {
                ...options.dataLabels,
                style: {
                    colors: ['#000000'],
                },
            },
            noData: {
                text: this.translocoService.translateObject('noStatData'),
            },
        };
    });
    public readonly heightClass = computed(() => {
        switch (this.options().chart.type) {
            case ChartTypeEnum.BAR:
            case ChartTypeEnum.LINE:
                return this.isMapExpanded() ? 'chart-line-bar_height' : 'chart-line-bar-expanded_height';
            case ChartTypeEnum.PIE:
            case ChartTypeEnum.RADIAL_BAR:
                return this.isMapExpanded() ? 'chart-pie-radial-bar_height' : 'chart-pie-radial-bar-expanded_height';
            default:
                return this.isMapExpanded() ? 'chart-pie-radial-bar_height' : 'chart-pie-radial-bar-expanded_height';
        }
    });
    public readonly isHeightClassApplied = input<boolean>(true);
    public readonly isMapExpanded = input<boolean>(true);
    public readonly options = input.required<BaseChartOptions>();

    private readonly chartContainer: Signal<ElementRef<HTMLDivElement>> =
        viewChild.required<ElementRef<HTMLDivElement>>('chartContainer');
    private resizeObserver: ResizeObserver | null = null;
    private readonly translocoService = inject(TranslocoService);

    public ngAfterViewInit(): void {
        this.updateChartWidth(this.chartContainer(), {
            set: (value: string) => this.chartWidth.set(value),
        });

        this.resizeObserver = this.createResizeObserver(this.chartContainer(), {
            set: (value: string) => this.chartWidth.set(value),
        });
    }

    public ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    private createResizeObserver(
        container: ElementRef<HTMLElement>,
        chartWidth: { set: (value: string) => void },
    ): ResizeObserver {
        const resizeObserver = new ResizeObserver(() => {
            this.updateChartWidth(container, chartWidth);
        });

        resizeObserver.observe(container.nativeElement);

        return resizeObserver;
    }

    private updateChartWidth(container: ElementRef<HTMLElement>, chartWidth: { set: (value: string) => void }): void {
        if (!container) {
            return;
        }

        const width = container.nativeElement.offsetWidth;

        const widthWithoutPadding = width - 48;
        chartWidth.set(`${widthWithoutPadding}px`);
    }
}
